import type { DataLayer } from 'assets/core/js/module/dataLayer'

const mediaQueries = MEDIA_QUERIES
const intlCache: Record<string, Intl.NumberFormat> = {}

export function yieldToMain(): Promise<void> {
  if ('scheduler' in window && 'yield' in window.scheduler) {
    return window.scheduler.yield()
  }

  return new Promise((resolve) => setTimeout(resolve, 0))
}

export function polyfills(): boolean {
  // requestSubmit is not supported by enough browsers so we keep it outside of the polyfills script
  if (!HTMLFormElement.prototype.requestSubmit) {
    HTMLFormElement.prototype.requestSubmit = function () {
      const submitBtn = document.createElement('input')
      submitBtn.type = 'submit'
      submitBtn.hidden = true
      this.appendChild(submitBtn)
      submitBtn.click()
      this.removeChild(submitBtn)
    }
  }

  return true
}

export function loadLink(url: string, id: string): Promise<unknown> {
  return new Promise((resolve, reject) => {
    const link = document.createElement('link')
    link.rel = 'stylesheet'
    if (id) {
      link.id = id
    }
    link.href = url
    link.addEventListener('error', reject)
    link.addEventListener('load', resolve)
    document.body.appendChild(link)
  })
}

export function loadScript(url: string, id: null | string, onLoad: () => void | Promise<void>): Promise<void> {
  return new Promise((resolve, reject) => {
    const script = document.createElement('script')
    script.src = url
    if (id) {
      script.id = id
    }
    script.addEventListener('error', reject)
    script.addEventListener('load', () => resolve(onLoad()))
    document.body.appendChild(script)
  })
}

/**
 * Custom listener axeptio listener to send events to our dataLayer
 * It registers a callback inside _axcb which is only used if the axeptio tag is present
 */
export function initAxeptio(dataLayer: Array<Record<string, unknown>> | DataLayer[]): void {
  if (!Array.isArray(window._axcb)) {
    window._axcb = []
  }
  // biome-ignore lint/suspicious/noExplicitAny: unknown
  window._axcb.push(function (axeptio: Record<string, any>): void {
    axeptio.on('cookies:complete', function (choices: Record<string, boolean>) {
      Object.keys(choices).forEach(function (serviceSlug) {
        void (async () => {
          if (choices[serviceSlug]) {
            const event: Record<string, unknown> = {
              event: `axeptio_activate_${serviceSlug}`,
            }
            event[serviceSlug] = true
            await yieldToMain()
            dataLayer.push(event)
          }
        })()
      })
    })
  })
}

export function initLinks(): void {
  const isExternalLink = (target: HTMLElement): boolean => {
    return (
      target.matches('.external-link') ||
      (!isMobile() && target.matches('[data-desktop-target]') && target.getAttribute('data-desktop-target') === '_blank')
    )
  }

  document.addEventListener('click', function (e: MouseEvent) {
    const target = e.target as HTMLElement
    const rowLinkEl = target.closest('.row-link')
    if (rowLinkEl && !target.classList.contains('no-link') && rowLinkEl.querySelector('.row-target')) {
      window.location.href = (rowLinkEl.querySelector('.row-target') as HTMLLinkElement).href
    }

    if (target.matches('[data-href]') && !isExternalLink(target) && target.hasAttribute('data-href')) {
      window.location.href = target.getAttribute('data-href') as string
    }

    if (isExternalLink(target)) {
      e.preventDefault()
      e.stopPropagation()

      try {
        const windowOpen = window.open()
        if (windowOpen) {
          windowOpen.opener = null
          windowOpen.location = target.getAttribute('data-href') || (target as HTMLLinkElement).href
        }
      } catch {
        window.location.href = target.getAttribute('data-href') || (target as HTMLLinkElement).href
      }
    }

    const rowExternalLinkEl = target.closest('.row-external-link')
    if (rowExternalLinkEl && !target.classList.contains('no-link')) {
      e.preventDefault()
      e.stopPropagation()

      const windowOpen = window.open()
      if (windowOpen) {
        windowOpen.opener = null
        windowOpen.location = (rowExternalLinkEl.querySelector('.row-target') as HTMLLinkElement).href
      }
    }
  })
}

export function getMediaQuerySize(size: string, withUnits = true): string | null {
  const mqSize = mediaQueries[size]

  if (!mqSize) {
    return null
  }

  return withUnits ? mqSize : mqSize.replace('px', '')
}

export function queryMedia(size: string): MediaQueryList | false {
  if (mediaQueries[size] && typeof window.matchMedia !== 'undefined') {
    return window.matchMedia(`(max-width: ${mediaQueries[size]})`)
  }

  return false
}

export function formatCurrency(number: number, currency: string | undefined, hideDecimals = false): string | number {
  currency = currency || document.body.dataset.currency || 'EUR'

  if (window.Intl) {
    const config: Intl.NumberFormatOptions = {
      currency,
      minimumFractionDigits: 0,
      style: 'currency',
    }

    if (number % 1 !== 0) {
      config.minimumFractionDigits = 2
    }

    if (hideDecimals) {
      config.maximumFractionDigits = 0
      config.minimumFractionDigits = 0
    }

    const locale = (document.querySelector('html') as HTMLElement).lang
    const cacheKey = `${currency}_${config.minimumFractionDigits || 0}_${hideDecimals.toString()}_${locale}`

    if (!intlCache[cacheKey]) {
      intlCache[cacheKey] = new window.Intl.NumberFormat(locale, config)
    }

    return intlCache[cacheKey].format(number)
  }

  return number
}

export function formatDate(value: string): string {
  const date = new Date(value)
  const locale = (document.querySelector('html') as HTMLElement).lang

  return date.toLocaleDateString(locale)
}

export function createXhr(verb: string, url: string): XMLHttpRequest {
  const xhr = new XMLHttpRequest()
  xhr.open(verb, url)
  xhr.setRequestHeader('X-Requested-With', 'XMLHttpRequest')

  if (verb === 'POST') {
    xhr.setRequestHeader('Content-Type', 'application/x-www-form-urlencoded')
  }

  return xhr
}

export async function createFetch(url: string, init?: RequestInit): Promise<Response> {
  const headers = new Headers(init?.headers)
  headers.set('X-Requested-With', 'XMLHttpRequest')

  return fetch(url, { ...init, headers })
}

export function showFlashMessage(message: string, type = 'error', timeout = 10 * 1000): void {
  const buttonEl = document.createElement('button')
  buttonEl.className = 'close'
  buttonEl.setAttribute('type', 'button')
  buttonEl.setAttribute('data-target', 'alert-flash')

  const messageEl = document.createElement('p')
  messageEl.innerHTML = message

  const alertEl = document.createElement('div')
  alertEl.className = `alert alert-${type}`
  alertEl.appendChild(buttonEl)
  alertEl.appendChild(messageEl)

  const alertContainerEl = document.createElement('div')
  alertContainerEl.setAttribute('id', 'alert-flash')
  alertContainerEl.appendChild(alertEl)

  document.body.insertBefore(alertContainerEl, document.body.firstChild)

  const alertFlashEl = document.getElementById('alert-flash')

  document.addEventListener('click', function (e) {
    if ((e.target as HTMLElement).matches('#alert-flash .close') && alertFlashEl && alertFlashEl.parentNode) {
      alertFlashEl.parentNode.removeChild(alertFlashEl)
    }
  })

  setTimeout(function () {
    if (alertFlashEl?.parentNode) {
      alertFlashEl.parentNode.removeChild(alertFlashEl)
    }
  }, timeout)
}

export function isMobile(): boolean {
  const size = getMediaQuerySize('mobile')

  if (!size) {
    return false
  }

  return window.matchMedia(`(max-width: ${size})`).matches
}

export function isTablet(): boolean {
  const size = getMediaQuerySize('tablet')

  if (!size) {
    return false
  }

  return window.matchMedia(`(max-width: ${size})`).matches
}

export function isDesktop(): boolean {
  const size = getMediaQuerySize('desktop')

  if (!size) {
    return false
  }

  return window.matchMedia(`(min-width: ${size})`).matches
}

export function isAndroid(): boolean {
  return navigator.userAgent.match(/Android/i) !== null
}

export function isIOS(): boolean {
  return navigator.userAgent.match(/iPhone|iPad|iPod/i) !== null
}

export function createNodeFromHTML(html: string): HTMLElement {
  const div = document.createElement('div')
  div.innerHTML = html
  return div.firstElementChild as HTMLElement
}

export function debounce<T extends unknown[]>(delay: number, fn: (...args: T) => void): () => void {
  let timerId: null | ReturnType<typeof setTimeout>
  return function (...args: T) {
    if (timerId) {
      clearTimeout(timerId)
    }
    timerId = setTimeout(() => {
      fn(...args)
      timerId = null
    }, delay)
  }
}

export async function loadIconsFont(): Promise<void> {
  if (!window.iconsFontPath) {
    return
  }

  const font = new FontFace('dca-icons', `url(${window.iconsFontPath}) format('woff2')`, {})

  document.fonts.add(font)

  await font.load()
}
